import type { UserManager, User } from 'oidc-client-ts';

/**
 * Organisatoren-Konto (docs/16). Drei Arten, wie eine Sitzung zustande kommt:
 *
 * - `direct` — eigene Anmeldemaske in der App, die direkt mit dem User Pool
 *   spricht (`cognito.ts`, docs/notes/anmeldung-eigene-maske.md). Der Regelweg:
 *   in der gewählten Sprache und im Airmail-Design.
 * - `hosted` — Cognito Hosted UI per OpenID Connect (Authorization Code + PKCE,
 *   oidc-client-ts). Bleibt als Notausgang über `hostedLogin()` erreichbar,
 *   wird aber nicht mehr von selbst aufgerufen.
 * - `dev` — Dev-Login mit E-Mail, der ein unsigniertes `dev.…`-Token erzeugt,
 *   das nur der lokale Dev-Server akzeptiert (VITE_AUTH=dev).
 *
 * Die App bleibt local-first: ohne Konto läuft ein Turnier auf dem Gerät;
 * das Konto braucht es für Cloud-Sync, Anmeldung und die Turnierliste in der Cloud.
 */
export interface AuthUser {
  sub: string;
  email?: string;
  idToken: string;
  /** Unix-ms */
  expiresAt: number;
}

const env = import.meta.env as Record<string, string | undefined>;
export const AUTH = {
  dev: env.VITE_AUTH === 'dev',
  authority: env.VITE_AUTH_AUTHORITY ?? '',
  clientId: env.VITE_AUTH_CLIENT_ID ?? '',
  domain: env.VITE_AUTH_DOMAIN ?? '',
};
/** Gibt es überhaupt einen Login (Build mit Cognito-Daten oder Dev-Modus)? */
export const authAvailable = AUTH.dev || (!!AUTH.authority && !!AUTH.clientId);

const DEV_KEY = 'airmail.devUser';
const RETURN_KEY = 'airmail.authReturn';

/** Woher die laufende Sitzung stammt — entscheidet über Erneuern und Abmelden */
export type AuthKind = 'dev' | 'direct' | 'hosted';

let current: AuthUser | null = null;
let kind: AuthKind | null = null;
const listeners = new Set<(u: AuthUser | null) => void>();
function set(u: AuthUser | null, k?: AuthKind | null): void {
  current = u;
  if (k !== undefined) kind = k;
  if (!u) kind = null;
  for (const l of listeners) l(u);
}
export function authKind(): AuthKind | null {
  return kind;
}
export function subscribe(fn: (u: AuthUser | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function currentUser(): AuthUser | null {
  return current;
}
/** Für API-Aufrufe: Authorization-Header, wenn angemeldet */
export function authHeader(): Record<string, string> {
  return current ? { authorization: `Bearer ${current.idToken}` } : {};
}

// --- OIDC (Cognito) ------------------------------------------------------------

let manager: UserManager | null = null;
function redirectUri(): string {
  return `${location.origin}${location.pathname}`;
}
/** Bibliothek erst laden, wenn wirklich ein Cognito-Login ansteht (hält das Hauptbündel klein) */
async function oidc(): Promise<UserManager> {
  if (!manager) {
    const { UserManager, WebStorageStateStore } = await import('oidc-client-ts');
    manager = new UserManager({
      authority: AUTH.authority,
      client_id: AUTH.clientId,
      redirect_uri: redirectUri(),
      post_logout_redirect_uri: redirectUri(),
      response_type: 'code',
      scope: 'openid email profile',
      automaticSilentRenew: false,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: localStorage }),
      // Cognito liefert keinen end_session_endpoint per Discovery — Logout läuft über die Hosted-UI-Domain
      metadataSeed: { end_session_endpoint: `https://${AUTH.domain}/logout` },
    });
  }
  return manager;
}
function fromOidc(u: User | null, abgelaufenOk = false): AuthUser | null {
  if (!u || !u.id_token || (u.expired && !abgelaufenOk)) return null;
  const out: AuthUser = { sub: u.profile.sub, idToken: u.id_token, expiresAt: (u.expires_at ?? 0) * 1000 };
  if (typeof u.profile.email === 'string') out.email = u.profile.email;
  return out;
}

// --- Dev-Login ------------------------------------------------------------------

function b64url(s: string): string {
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/*
 * Nachgestellter zweiter Faktor für den Dev-Modus (docs/notes/zweifaktor.md):
 * Es gibt keinen Pool, also merkt sich die App je E-Mail nur, ob TOTP „an" ist
 * (`localStorage`, damit ein Neuladen zwischen Ab- und Anmelden nichts
 * vergisst). Als Code gilt einzig `000000`, als Geheimnis ein fester Text.
 * Nur damit die Oberfläche — Konto-Abschnitt und Code-Schritt — ohne Cognito
 * bedienbar und im Playwright-Lauf prüfbar ist. Der Betrieb kennt das nicht.
 */
const DEV_MFA_KEY = 'airmail.devMfa';
export const DEV_MFA_CODE = '000000';
const DEV_MFA_SECRET = 'AIRMAILDEVAIRMAILDEVAIRMAILDEVAI';
function devMfaListe(): string[] {
  try {
    const raw = localStorage.getItem(DEV_MFA_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
/** Dev-Modus: Verlangt dieses Konto beim Anmelden einen Code? */
export function devMfaAktiv(email: string): boolean {
  return AUTH.dev && devMfaListe().includes(email.trim().toLowerCase());
}
function devMfaSetzen(email: string, an: boolean): void {
  const rest = devMfaListe().filter((e) => e !== email);
  try {
    localStorage.setItem(DEV_MFA_KEY, JSON.stringify(an ? [...rest, email] : rest));
  } catch {
    /* egal */
  }
}

/** Dev-Login. Steht das Konto auf „zweiter Faktor an", muss der Code `000000` mitkommen — sonst `CodeMismatchException` */
export function devLogin(email: string, code?: string): AuthUser {
  const clean = email.trim().toLowerCase();
  if (devMfaAktiv(clean) && (code ?? '').trim() !== DEV_MFA_CODE) {
    const err = new Error('Invalid code received for user');
    err.name = 'CodeMismatchException';
    throw err;
  }
  let h = 0;
  for (const ch of clean) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const sub = `dev-${h.toString(16)}`;
  const u: AuthUser = { sub, email: clean, idToken: `dev.${b64url(JSON.stringify({ sub, email: clean }))}`, expiresAt: Date.now() + 365 * 24 * 3600 * 1000 };
  try {
    localStorage.setItem(DEV_KEY, JSON.stringify(u));
  } catch {
    /* egal */
  }
  set(u, 'dev');
  return u;
}

// --- Eigene Anmeldemaske (Cognito ohne Weiterleitung) ------------------------------

/** Die Maske in `pages/Login.tsx` meldet die fertige Sitzung hier an (docs/notes/anmeldung-eigene-maske.md) */
export function adoptDirect(u: AuthUser): void {
  set(u, 'direct');
}

/**
 * Sprachwechsel am Konto nachziehen: das Attribut `locale` bestimmt, in welcher
 * Sprache Cognito die nächste Mail schickt. Nur für die eigene Maske — nach der
 * Hosted UI liegt keine Cognito-Sitzung im Speicher. Fehler bleiben still.
 */
export async function syncLocale(lang: string): Promise<void> {
  if (kind !== 'direct') return;
  try {
    await (await import('./cognito')).setLocale(lang);
  } catch {
    /* egal — die Sprache in der App gilt trotzdem */
  }
}

// --- Zweiter Faktor (TOTP) — Konto-Seite, docs/notes/zweifaktor.md ------------------

/**
 * Gibt es für diese Sitzung eine Zwei-Faktor-Verwaltung? Nur die eigene Maske
 * (Cognito-Sitzung im Speicher) und der Dev-Modus — nach der Hosted UI hält
 * die App keinen Access-Token, mit dem sich das Konto umstellen ließe.
 */
export function zweiFaktorVerfuegbar(): boolean {
  return kind === 'dev' || kind === 'direct';
}

/** Ist am angemeldeten Konto ein zweiter Faktor eingerichtet? */
export async function zweiFaktorStatus(): Promise<boolean> {
  if (kind === 'dev') return !!current?.email && devMfaAktiv(current.email);
  return (await import('./cognito')).mfaStatus();
}

/**
 * Einrichten beginnen: Geheimnis vom Dienst holen und die Adresse für die
 * Authenticator-App bauen (`otpauth://totp/Airmail:<email>?secret=…&issuer=Airmail`).
 * Eingeschaltet wird der Faktor erst mit `zweiFaktorBestaetigen()`.
 */
export async function zweiFaktorEinrichten(): Promise<{ secret: string; uri: string }> {
  const secret = kind === 'dev' ? DEV_MFA_SECRET : await (await import('./cognito')).mfaEinrichten();
  const konto = current?.email ?? current?.sub ?? '';
  const uri = `otpauth://totp/${encodeURIComponent(`Airmail:${konto}`)}?secret=${secret}&issuer=Airmail`;
  return { secret, uri };
}

/** Ersten Code prüfen und den Faktor einschalten */
export async function zweiFaktorBestaetigen(code: string): Promise<void> {
  if (kind === 'dev') {
    if (code.trim() !== DEV_MFA_CODE) {
      const err = new Error('Code mismatch');
      err.name = 'EnableSoftwareTokenMFAException';
      throw err;
    }
    if (current?.email) devMfaSetzen(current.email, true);
    return;
  }
  await (await import('./cognito')).mfaBestaetigen(code);
}

/** Faktor abschalten — ab der nächsten Anmeldung reicht wieder das Passwort */
export async function zweiFaktorAbschalten(): Promise<void> {
  if (kind === 'dev') {
    if (current?.email) devMfaSetzen(current.email, false);
    return;
  }
  await (await import('./cognito')).mfaAbschalten();
}

// --- Lebenszyklus ---------------------------------------------------------------

/**
 * Beim Start: erst eine Sitzung der eigenen Maske wiederherstellen, sonst wie
 * bisher — Rücksprung von der Hosted UI verarbeiten (Code in der URL) bzw. den
 * gespeicherten OIDC-Nutzer laden und bei Bedarf still erneuern.
 * Liefert die Route, zu der nach dem Login zurückgesprungen werden soll.
 */
export async function initAuth(): Promise<{ user: AuthUser | null; returnTo?: string }> {
  if (!authAvailable) return { user: null };
  if (AUTH.dev) {
    try {
      const raw = localStorage.getItem(DEV_KEY);
      if (raw) set(JSON.parse(raw) as AuthUser, 'dev');
    } catch {
      /* egal */
    }
    return { user: current };
  }
  const params = new URLSearchParams(location.search);
  if (params.has('code') && params.has('state')) {
    try {
      const u = await (await oidc()).signinCallback();
      set(fromOidc(u ?? null), 'hosted');
    } catch (e) {
      console.warn('Login-Rücksprung fehlgeschlagen', e);
    }
    // Query entfernen, Hash-Route behalten
    let returnTo: string | undefined;
    try {
      returnTo = sessionStorage.getItem(RETURN_KEY) ?? undefined;
      sessionStorage.removeItem(RETURN_KEY);
    } catch {
      /* egal */
    }
    history.replaceState(null, '', `${redirectUri()}${returnTo ? `#${returnTo}` : location.hash}`);
    const out: { user: AuthUser | null; returnTo?: string } = { user: current };
    if (returnTo) out.returnTo = returnTo;
    return out;
  }
  // Eigene Maske zuerst: liegt eine Cognito-Sitzung auf dem Gerät, bleibt die
  // Hosted UI (und damit oidc-client-ts) ganz aus dem Spiel.
  try {
    const u = await (await import('./cognito')).restoreSession();
    if (u) {
      set(u, 'direct');
      return { user: current };
    }
  } catch {
    /* egal — dann eben der bisherige Weg */
  }
  try {
    const m = await oidc();
    const gespeichert = await m.getUser();
    let u = gespeichert;
    let offline = false;
    if (u && (u.expired || (u.expires_at ?? 0) * 1000 - Date.now() < 5 * 60_000)) {
      u = await m.signinSilent().catch((e: unknown) => {
        // Nur am Netz gescheitert: mit dem gespeicherten (abgelaufenen) Nutzer weiter, später erneuern
        offline = !oidcAuthFehler(e);
        return null;
      });
    }
    set(u ? fromOidc(u) : offline ? fromOidc(gespeichert, true) : null, 'hosted');
  } catch {
    set(null);
  }
  return { user: current };
}

/** Token kurz vor Ablauf erneuern (Refresh-Token) — vor API-Aufrufen */
export async function ensureFresh(): Promise<AuthUser | null> {
  if (!current || kind === 'dev' || AUTH.dev) return current;
  if (current.expiresAt - Date.now() > 60_000) return current;
  /*
   * Abgemeldet wird nur, wenn die Sitzung wirklich vorbei ist. Ein Netzfehler
   * beim Erneuern (Halle ohne WLAN) meldete vorher ab — der Konto-Cache flog
   * raus und die Pro-Funktionen waren mitten im Turnier gesperrt. Jetzt bleibt
   * die Person angemeldet; der nächste Aufruf mit Netz erneuert das Token.
   */
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return current;
  if (kind === 'direct') {
    try {
      set(await (await import('./cognito')).refresh(), 'direct');
    } catch (e) {
      if (!istOffline(e)) set(null);
    }
    return current;
  }
  try {
    const u = await (await oidc()).signinSilent();
    set(fromOidc(u), 'hosted');
  } catch (e) {
    if (oidcAuthFehler(e)) set(null);
  }
  return current;
}

/** Scheiterte das Erneuern der eigenen Maske nur am Netz? */
function istOffline(e: unknown): boolean {
  return e instanceof Error && e.name === 'OfflineFehler';
}

/**
 * OIDC: Hat der Server die Sitzung abgelehnt (`login_required`,
 * `invalid_grant` …)? Dann trägt der Fehler ein `error`-Feld — Zeitüberschreitung
 * und Netzfehler tun das nicht und melden deshalb nicht ab.
 */
function oidcAuthFehler(e: unknown): boolean {
  return !!e && typeof e === 'object' && typeof (e as { error?: unknown }).error === 'string';
}

/** Zur Anmeldung — eigene Maske unter `#/login` (im Dev-Modus dieselbe Seite). `returnTo` = Hash-Route ohne '#'. */
export function login(returnTo?: string): Promise<void> {
  const target = returnTo ?? location.hash.replace(/^#/, '') ?? '/';
  location.hash = `#/login?returnTo=${encodeURIComponent(target)}`;
  return Promise.resolve();
}

/** Notausgang: doch über die Cognito Hosted UI anmelden (Weiterleitung, Sprache der Hosted UI) */
export async function hostedLogin(returnTo?: string): Promise<void> {
  const target = returnTo ?? location.hash.replace(/^#/, '') ?? '/';
  try {
    sessionStorage.setItem(RETURN_KEY, target);
  } catch {
    /* egal */
  }
  await (await oidc()).signinRedirect();
}

/**
 * Das Cognito-Konto selbst löschen (nach `deleteAccount` auf dem Server).
 * Dev-Login: nur den lokalen Merker entfernen. Eigene Maske: über die
 * Bibliothek. Hosted UI: Cognito-API direkt mit dem Access-Token — die
 * OIDC-Bibliothek hat ihn, die App sonst nirgends.
 */
export async function deleteCognitoUser(): Promise<void> {
  if (AUTH.dev || kind === 'dev') {
    try {
      localStorage.removeItem(DEV_KEY);
    } catch {
      /* egal */
    }
    set(null);
    return;
  }
  if (kind === 'direct') {
    await (await import('./cognito')).deleteUser();
    set(null);
    return;
  }
  const u = await (await oidc()).getUser();
  if (u?.access_token) {
    const r = await fetch(`${AUTH.authority.replace(/\/[^/]*$/, '')}/`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-amz-json-1.1', 'x-amz-target': 'AWSCognitoIdentityProviderService.DeleteUser' },
      body: JSON.stringify({ AccessToken: u.access_token }),
    });
    if (!r.ok) throw new Error(`Konto konnte nicht gelöscht werden (HTTP ${r.status})`);
  }
  await (await oidc()).removeUser();
  set(null);
}

export async function logout(): Promise<void> {
  if (AUTH.dev || kind === 'dev') {
    try {
      localStorage.removeItem(DEV_KEY);
    } catch {
      /* egal */
    }
    set(null);
    return;
  }
  if (kind === 'direct') {
    try {
      await (await import('./cognito')).signOut();
    } catch {
      /* egal */
    }
    set(null);
    return;
  }
  try {
    await (await oidc()).removeUser();
  } finally {
    set(null);
    // Cognito-Logout: beendet die Hosted-UI-Sitzung, springt zurück zur App
    location.assign(`https://${AUTH.domain}/logout?client_id=${encodeURIComponent(AUTH.clientId)}&logout_uri=${encodeURIComponent(redirectUri())}`);
  }
}
